// The number on the home-screen icon: how many chores are waiting.
//
// Bug report (Matt, 2026-09-27): "This app never shows badges when it has
// things I need to do." Only a chore push ever SET the badge, and opening the
// app cleared it outright — so with the app used daily, the icon was blank
// while stickiness ratings, a tiebreak and award years sat waiting.
//
// Now the app sets it itself, from the same digest the push reads, with the
// same arithmetic as the Lambda's badge (aws-lambda/pushCadence.js
// dueFromDigest): stickiness films + 1 for a tiebreak + each award year.
// A prompt that's disabled, or paused by its daily quota, isn't counted —
// the badge must never promise work the app won't show when you open it.
// pushCadence.js is CommonJS for the Lambda, so this is a mirror rather than
// an import; src/test/appBadge.test.js pins the two together.
//
// Showtimes (2026-10-05): with the "Showtimes waiting" switch on - off by
// default, so only `prefs.showtimes === true` counts - every film still
// waiting on the Showtimes screen adds one (showtimesUnread.js).
//
// Friend requests (2026-10-09): each one waiting for an answer adds one,
// always — a person waiting isn't a chore with a switch. Friends who had
// just joined never answered Matt's requests; nothing outside the app said
// one was there. (iOS only shows the badge once notifications are allowed.)
//
// Film Club activity is otherwise deliberately not counted here. A friend-log push adds
// one to the badge; the app setting the chore count on open is what drops it.

import { isNoticeDismissed, noticeDay } from './homeNotices.js';
import { chorePauseEnd } from './chorePause.js';
import { unsnoozedFriendRequests } from './friendRequests.js';

export function appBadgeCount (digest, prefs = {}, now = Date.now(), showtimes = 0, friendRequests = 0) {
  const open = (section) => {
    const at = Number(section?.eligibleAt);
    return !Number.isFinite(at) || at <= now;
  };

  const pinned = Boolean(digest?.tiebreak?.pinned) && prefs?.tiebreak !== false;

  const stickiness = (prefs?.stickiness === false || pinned || !open(digest?.stickiness))
    ? 0
    : (digest?.stickiness?.count || 0) +
      (digest?.stickiness?.dueTimes || []).filter((time) => time <= now).length;

  const tiebreak = (prefs?.tiebreak !== false && open(digest?.tiebreak) && digest?.tiebreak?.due) ? 1 : 0;

  const awards = (prefs?.awards === false || pinned || !open(digest?.awards))
    ? 0
    : (digest?.awards?.years || []).length;

  const films = prefs?.showtimes === true ? (Number(showtimes) || 0) : 0;

  return stickiness + tiebreak + awards + films + (Number(friendRequests) || 0);
}

// What Home has put away doesn't count (bug report, Matt, 2026-10-30: "I've
// got a red notification that's just been stuck here … and I don't know how
// to get rid of it"). The X on the Showtimes or friend-request card, and "Not
// now" on a request, hid the card while the icon kept counting it, so the
// number pointed at nothing on screen. Same rule as the cards
// (homeNotices.js): hidden until tomorrow, or until the count grows / a new
// request arrives. Per device, like the cards; the chores go further and
// pause on the account (chorePause.js).
export function badgeExtrasShown ({ films = 0, friendRequests = [], dismissals = {}, snoozes = {}, now = Date.now() }) {
  const shownRequests = unsnoozedFriendRequests(friendRequests, snoozes, now);
  const requestSignature = shownRequests.map((request) => request.key).sort().join(',');
  const filmCount = Number(films) || 0;
  return {
    films: filmCount && isNoticeDismissed(dismissals, 'showtimes', filmCount, now) ? 0 : filmCount,
    friendRequests: shownRequests.length && isNoticeDismissed(dismissals, 'friendRequest', requestSignature, now)
      ? 0
      : shownRequests.length
  };
}

// The same rule, for the push Lambda (report 2026-11-02: "a 52 number on the
// red dot … then if I click in, it goes away"). Every push carries a badge,
// and the Lambda counted every Showtimes film, put away or not, so a push
// set ~50 and the app's recount on open took it back down. The digest now
// carries what this device has put away: `{ upTo, until }` = the count the
// card was dismissed at and the local midnight it comes back. The Lambda's
// `showtimesBadgeFilms` (pushCadence.js) applies it; appBadge.test.js pins
// the two to the same answer. Per device: the last phone to publish decides.
export function badgeHiddenForDigest (dismissals = {}, now = Date.now()) {
  const record = dismissals?.showtimes;
  if (!record || record.day !== noticeDay(now) || typeof record.signature !== 'number') return {};
  return { showtimes: { upTo: record.signature, until: chorePauseEnd(now) } };
}
